/**
 * P1-11 · Client-side upload validation + display formatting.
 *
 * Pure functions shared by the library and the picker. Validation here is UX
 * (reject before a byte is sent, with a reason); the router re-enforces the
 * same limits server-side. A *rejected* file is deterministic — retrying
 * re-fails identically, so rejection is dismiss-only. Transient transfer
 * errors are a separate "failed" state and are the only retryable ones.
 */

export interface MediaUploadLimits {
  allowedMimeTypes: string[];
  maxUploadBytes: number;
}

/** Human-readable rejection reason, or null when the file is acceptable. */
export function rejectionReason(
  file: { name: string; type: string; size: number },
  limits: MediaUploadLimits,
): string | null {
  if (!limits.allowedMimeTypes.includes(file.type)) {
    const label = file.type
      ? (file.type.split("/")[1] ?? file.type).toUpperCase()
      : "unknown type";
    return `Unsupported type — ${label}. Use ${supportedLabel(limits.allowedMimeTypes)}.`;
  }
  if (file.size > limits.maxUploadBytes) {
    return `Too large — ${formatBytes(file.size)} exceeds the ${formatBytes(limits.maxUploadBytes)} limit.`;
  }
  return null;
}

/** "JPEG, PNG, WebP…" from a mime allow-list, for dropzone hint copy. */
export function supportedLabel(mimeTypes: string[]): string {
  const names: Record<string, string> = {
    "image/jpeg": "JPEG",
    "image/png": "PNG",
    "image/webp": "WebP",
    "image/avif": "AVIF",
    "image/svg+xml": "SVG",
  };
  return mimeTypes.map((m) => names[m] ?? m).join(", ");
}

/** 1536 → "1.5 KB"; 18_400_000 → "18.4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${stripZero((bytes / 1024).toFixed(1))} KB`;
  return `${stripZero((bytes / (1024 * 1024)).toFixed(1))} MB`;
}

function stripZero(s: string): string {
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}
